/**
 * The Speeds on the catalogue item page (package N3, spec
 * docs/features/v1-completion/N_time_spec.md §6; mock-up
 * mockups/N_time_speeds_and_dates.html states 1-5).
 *
 * One list replaces the old Speed picker and the route-and-tier options
 * panel: each Speed with its arrival and price, the default and why, "need it
 * quicker", a needed-by date with a fixed-date tick, and the route options
 * behind each Speed when the preview carries them (#90), any of which
 * staff may choose with a reason (#2). Every supplier, in-house included, is
 * listed the same way. A Speed means arrival (d64).
 *
 * The backend decides every state shown here and words every date (Time):
 * the list renders it through the shared-core date component.
 */
import { useState } from "react";
import { versionsPriceLabel } from "@shared-core/domain/line-versions";
import type {
  CataloguePriceOption,
  CataloguePriceOptions,
  RouteOverrideReason,
} from "@shared-core/data/supplier-pricing-types";
import { ROUTE_OVERRIDE_REASONS } from "@shared-core/data/supplier-pricing-types";
import type { SpeedChoice, SpeedOffer } from "@shared-core/data/speed-types";
import {
  optionsBehindSpeed,
  optionsInNoSpeed,
  quickerDifference,
  speedDate,
  speedListHint,
} from "@shared-core/domain/speed-presentation";
import {
  deliveryLabel,
  priceOptionTitle,
  type PriceOptionsNotice,
  type RouteOverrideErrors,
} from "@shared-core/domain/supplier-pricing-presentation";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { pendingRouteLine } from "@shared-core/domain/price-still-coming";
import { Info } from "lucide-react";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Checkbox } from "../../primitives/checkbox";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import { Textarea } from "../../primitives/textarea";

export interface SpeedListProps {
  speeds: SpeedChoice;
  /** The route options behind the Speeds; null where none are served (a request with none recorded, or no route controls wired). */
  priceOptions: CataloguePriceOptions | null;
  neededBy: string | null;
  neededByIsHard: boolean;
  onNeededByChange: (neededBy: string | null) => void;
  onNeededByIsHardChange: (isHard: boolean) => void;
  onSelectSpeed: (speedCode: string) => void;
  /** A route option chosen behind a Speed (#2): needs a reason when it is not the Speed's own. */
  onSelectOption: (option: CataloguePriceOption, speedCode: string) => void;
  overrideReason: RouteOverrideReason | null;
  overrideNote: string;
  onOverrideReasonChange: (reason: RouteOverrideReason | null) => void;
  onOverrideNoteChange: (note: string) => void;
  /** The backend's refusal of the last Send / Place order, bound to its input. */
  overrideErrors?: RouteOverrideErrors;
  /** A change since the last preview the CSR should be told about. */
  notice?: PriceOptionsNotice | null;
}

export function SpeedList({
  speeds,
  priceOptions,
  neededBy,
  neededByIsHard,
  onNeededByChange,
  onNeededByIsHardChange,
  onSelectSpeed,
  onSelectOption,
  overrideReason,
  overrideNote,
  onOverrideReasonChange,
  onOverrideNoteChange,
  overrideErrors = {},
  notice,
}: SpeedListProps) {
  const [shownBehind, setShownBehind] = useState<string | null>(null);
  const selected = speeds.offers.find(
    (o) => o.speedCode === speeds.selectedCode,
  );
  const quickerNote = quickerDifference(speeds, formatQuotedPrice);
  const behind = speeds.offers.find((o) => o.speedCode === shownBehind);
  return (
    <div className="space-y-3" data-testid="speed-list">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="needed-by">
            Needed by{" "}
            <span className="text-xs font-normal text-muted-foreground">
              (optional)
            </span>
          </Label>
          <Input
            id="needed-by"
            type="date"
            value={neededBy ?? ""}
            onChange={(e) => onNeededByChange(e.target.value || null)}
            className="w-48"
          />
          <label className="flex items-center gap-2 pt-1 text-xs text-muted-foreground">
            <Checkbox
              checked={neededByIsHard}
              disabled={neededBy == null}
              onCheckedChange={(v) => onNeededByIsHardChange(v === true)}
              aria-label="Fixed date: it must arrive by then"
            />
            Fixed date: it must arrive by then
          </label>
        </div>
      </div>

      <div className="space-y-1">
        <div className="text-sm font-medium">Speed</div>
        <p className="text-xs text-muted-foreground">
          {speedListHint(speeds, selected?.arrival.basis ?? null)}
        </p>
      </div>

      {notice && <NoticeLine notice={notice} />}

      <RadioGroup
        aria-label="Speed"
        value={speeds.selectedCode ?? undefined}
        onValueChange={onSelectSpeed}
        className="gap-2"
      >
        {speeds.offers.map((offer) => (
          <SpeedRow
            key={offer.speedCode}
            offer={offer}
            isSelected={offer.speedCode === speeds.selectedCode}
            isDefault={offer.speedCode === speeds.defaultCode}
            isQuicker={offer.speedCode === speeds.quickerCode}
            quickerNote={
              offer.speedCode === speeds.quickerCode ? quickerNote : null
            }
            priceOptions={priceOptions}
            isShowingBehind={shownBehind === offer.speedCode}
            onToggleBehind={() =>
              setShownBehind((s) =>
                s === offer.speedCode ? null : offer.speedCode,
              )
            }
          />
        ))}
        {speeds.unavailable.map((u) => (
          <div
            key={u.speedCode}
            className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 rounded-md border border-border px-3 py-2 text-sm opacity-60"
          >
            <RadioGroupItem
              value={u.speedCode}
              disabled
              aria-label={u.label}
              className="mt-0.5"
            />
            <span className="min-w-0">
              <span className="block font-medium">{u.label}</span>
              <span className="block text-xs text-muted-foreground">
                {u.reason}.
              </span>
            </span>
            <span className="text-muted-foreground">—</span>
          </div>
        ))}
      </RadioGroup>

      {priceOptions && behind && (
        <OptionsBehind
          offer={behind}
          priceOptions={priceOptions}
          onHide={() => setShownBehind(null)}
          onChoose={(option) => onSelectOption(option, behind.speedCode)}
        />
      )}

      {priceOptions?.isProvisional && (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Provisional:</span>{" "}
          {priceOptions.defaultReason}.
        </p>
      )}

      {priceOptions && priceOptions.unavailable.length > 0 && (
        <ul className="space-y-0.5 text-xs text-muted-foreground">
          {priceOptions.unavailable.map((u) =>
            // A route withheld for its delivery says so in a whole sentence, which names the route (doc 107).
            u.isDeliveryNotPriced ? (
              <li key={u.routeCode} className="text-foreground">
                {u.reason}
              </li>
            ) : (
              <li key={u.routeCode}>
                Not offered:{" "}
                <span className="text-foreground">{u.routeLabel}</span> —{" "}
                {u.reason}
              </li>
            ),
          )}
        </ul>
      )}

      {priceOptions && priceOptions.pending.length > 0 && (
        <ul
          className="space-y-0.5 text-xs text-muted-foreground"
          data-testid="pending-routes"
          aria-live="polite"
        >
          {priceOptions.pending.map((route) => (
            <li key={route.routeCode}>{pendingRouteLine(route)}</li>
          ))}
        </ul>
      )}

      {priceOptions?.overrideRequirement && (
        <OverrideReason
          reason={overrideReason}
          note={overrideNote}
          errors={overrideErrors}
          onReasonChange={onOverrideReasonChange}
          onNoteChange={onOverrideNoteChange}
        />
      )}
    </div>
  );
}

function SpeedRow({
  offer,
  isSelected,
  isDefault,
  isQuicker,
  quickerNote,
  priceOptions,
  isShowingBehind,
  onToggleBehind,
}: {
  offer: SpeedOffer;
  isSelected: boolean;
  isDefault: boolean;
  isQuicker: boolean;
  quickerNote: string | null;
  priceOptions: CataloguePriceOptions | null;
  isShowingBehind: boolean;
  onToggleBehind: () => void;
}) {
  const id = `speed-${offer.speedCode}`;
  const date = speedDate(offer);
  const option = priceOptions?.options.find((o) => o.key === offer.optionKey);
  const moreOptions = priceOptions
    ? optionsBehindSpeed(priceOptions, offer).length - 1
    : 0;
  return (
    <div
      data-testid={`speed-row-${offer.speedCode}`}
      className={`grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 rounded-md border px-3 py-2 text-sm ${
        isSelected
          ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/5"
          : "border-border"
      }`}
    >
      <RadioGroupItem
        id={id}
        value={offer.speedCode}
        aria-label={offer.label}
        className="mt-0.5"
      />
      <span className="min-w-0 space-y-0.5">
        <Label
          htmlFor={id}
          className="flex cursor-pointer flex-wrap items-center gap-1.5 font-medium"
        >
          {offer.label}
          {isDefault && <Badge variant="secondary">Default</Badge>}
          {isQuicker && <Badge variant="outline">Need it quicker</Badge>}
          {date.isToBeConfirmed && (
            <Badge variant="warning">Check with our team</Badge>
          )}
        </Label>
        <span className="block">{date.arrival}</span>
        {option && (
          <span className="block text-xs text-muted-foreground">
            via {priceOptionTitle(option)}
            {offer.ruleNote && ` · ${offer.ruleNote}`}
            {moreOptions > 0 &&
              ` · ${moreOptions} more option${moreOptions === 1 ? "" : "s"} `}
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={onToggleBehind}
            >
              {isShowingBehind ? "Hide" : "Show"}
            </Button>
          </span>
        )}
        {!option && offer.ruleNote && (
          <span className="block text-xs text-muted-foreground">
            {offer.ruleNote}
          </span>
        )}
        {quickerNote && (
          <span className="block text-xs text-muted-foreground">
            {quickerNote}
          </span>
        )}
        {(isSelected || date.isToBeConfirmed) && date.note && (
          <span className="block text-xs text-muted-foreground">
            {date.note}
          </span>
        )}
      </span>
      <span className="text-right font-medium tabular-nums">
        {formatQuotedPrice(offer.pricePence)}
      </span>
    </div>
  );
}

function OptionsBehind({
  offer,
  priceOptions,
  onHide,
  onChoose,
}: {
  offer: SpeedOffer;
  priceOptions: CataloguePriceOptions;
  onHide: () => void;
  onChoose: (option: CataloguePriceOption) => void;
}) {
  const rows = optionsBehindSpeed(priceOptions, offer);
  const inNoSpeed = optionsInNoSpeed(priceOptions);
  return (
    <div
      className="space-y-2 rounded-md border border-border p-3"
      data-testid="options-behind-speed"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium">Behind {offer.label}</div>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={onHide}
        >
          Hide
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Every option that arrives within {offer.label}&apos;s{" "}
        {offer.workingDaysToArrival} working day
        {offer.workingDaysToArrival === 1 ? "" : "s"}. The default is the
        cheapest, delivery included. Choosing another needs a reason. Every
        supplier, us included, is listed the same way.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className="py-1 pr-2 font-normal">Supplier</th>
              <th className="py-1 pr-2 font-normal">Tier</th>
              <th className="py-1 pr-2 font-normal">Arrival</th>
              <th className="py-1 pr-2 font-normal">Date from</th>
              <th className="py-1 pr-2 text-right font-normal">Price</th>
              <th className="py-1 pr-2 text-right font-normal">Delivery</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.key} className="border-t border-border align-top">
                <td className="py-1.5 pr-2">
                  {o.routeLabel}
                  {versionsPriceLabel(
                    o.versionQuantities?.length ?? 1,
                    o.versionPrices,
                  ) && (
                    <span className="block text-xs text-muted-foreground">
                      {versionsPriceLabel(
                        o.versionQuantities?.length ?? 1,
                        o.versionPrices,
                      )}
                    </span>
                  )}
                  {o.isExpiring && (
                    <Badge
                      variant="warning"
                      className="ml-1.5"
                      title="Last night's refresh of this quote failed. It is still inside its 36-hour life, so it can be sent."
                    >
                      Quote expiring
                    </Badge>
                  )}
                  {o.listNotice && (
                    <Badge
                      variant="warning"
                      className="ml-1.5"
                      title="The supplier's price list is running out or has run out. The price still stands; check it before sending."
                    >
                      {o.listNotice}
                    </Badge>
                  )}
                </td>
                <td className="py-1.5 pr-2">{o.tier}</td>
                <td className="py-1.5 pr-2">{o.arrival?.sentence}</td>
                <td className="py-1.5 pr-2 text-xs text-muted-foreground">
                  {o.arrival?.dateFrom}
                </td>
                <td className="py-1.5 pr-2 text-right tabular-nums">
                  {formatQuotedPrice(o.pricePence)}
                  {/* Cost follows the breakdown's cost gate: absent, prices only. */}
                  {o.costPence != null && (
                    <span className="block text-xs text-muted-foreground">
                      cost {formatQuotedPrice(o.costPence)}
                      {o.contributionPct != null &&
                        ` · ${o.contributionPct}% margin`}
                    </span>
                  )}
                </td>
                <td className="py-1.5 pr-2 text-right text-xs">
                  {deliveryLabel(o.delivery, formatQuotedPrice)}
                </td>
                <td className="py-1.5 text-right">
                  {o.key === priceOptions.selectedKey ? (
                    <Badge variant="secondary">
                      {o.key === offer.optionKey ? "Default" : "Chosen"}
                    </Badge>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => onChoose(o)}
                    >
                      Choose
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {inNoSpeed.length > 0 && (
        <ul className="space-y-0.5 text-xs text-muted-foreground">
          {inNoSpeed.map(({ option, why }) => (
            <li key={option.key}>
              In no Speed: {priceOptionTitle(option)} {why}.
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OverrideReason({
  reason,
  note,
  errors,
  onReasonChange,
  onNoteChange,
}: {
  reason: RouteOverrideReason | null;
  note: string;
  errors: RouteOverrideErrors;
  onReasonChange: (reason: RouteOverrideReason | null) => void;
  onNoteChange: (note: string) => void;
}) {
  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
      <Label htmlFor="route-override-reason">
        Why a different option to the default?
      </Label>
      <Select
        value={reason ?? undefined}
        onValueChange={(v) => onReasonChange(v as RouteOverrideReason)}
      >
        <SelectTrigger
          id="route-override-reason"
          className="w-full bg-background"
          aria-invalid={errors.reason != null}
          aria-describedby={
            errors.reason ? "route-override-reason-error" : undefined
          }
        >
          <SelectValue placeholder="Choose a reason…" />
        </SelectTrigger>
        <SelectContent>
          {ROUTE_OVERRIDE_REASONS.map((r) => (
            <SelectItem key={r.value} value={r.value}>
              {r.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {errors.reason && (
        <p id="route-override-reason-error" className="text-sm text-red-600">
          {errors.reason}
        </p>
      )}
      <Textarea
        aria-label="Note"
        placeholder={
          reason === "other"
            ? "Say what the reason is (required for Other)"
            : "Add a note (optional)"
        }
        value={note}
        onChange={(e) => onNoteChange(e.target.value)}
        rows={2}
        className="bg-background"
        aria-invalid={errors.note != null}
        aria-describedby={errors.note ? "route-override-note-error" : undefined}
      />
      {errors.note && (
        <p id="route-override-note-error" className="text-sm text-red-600">
          {errors.note}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Recorded on the estimate with the options that were offered.
      </p>
    </div>
  );
}

function NoticeLine({ notice }: { notice: PriceOptionsNotice }) {
  const text =
    notice.kind === "default_changed"
      ? `Default changed to ${notice.to} now delivery is priced (it was ${notice.from}).`
      : `${notice.choice} is no longer offered${
          notice.reason ? `: ${notice.reason}` : ""
        }. Choose another option.`;
  return (
    <Alert
      role="status"
      className="border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
    >
      <Info />
      <AlertDescription className="text-current">{text}</AlertDescription>
    </Alert>
  );
}
